import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  ImageOff,
  Loader2,
  RefreshCw,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { withBackend } from '../utils/api';

// Workspace for decks generated natively onto a PowerPoint master template.
// Generate mode: POSTs the outline once on mount, shows a patient loader,
// then becomes a pure viewer. View mode: `saved` prop renders the viewer
// immediately with no network calls. The output is already a real .pptx,
// so there is no export step — just a download link.
const NativePPTWorkspace = forwardRef(function NativePPTWorkspace(
  {
    nativeTemplateId,
    outlineSlides,
    deckTitle,
    model,
    userId,
    saved,
    onProjectCreated,
  },
  ref
) {
  // status: 'generating' (no slides yet) -> 'preview' (first render arrived,
  // QA/saving still running) -> 'ready' (complete). 'error' on failure.
  const [status, setStatus] = useState(saved ? 'ready' : 'generating');
  const [stage, setStage] = useState('planning');
  const [result, setResult] = useState(null);
  const [livePreviews, setLivePreviews] = useState([]);
  const [liveTitle, setLiveTitle] = useState(null);
  const [error, setError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [presentMode, setPresentMode] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  // Regenerate-with-notes: the only way to change a rendered .pptx is to
  // re-run the native engine, steered by the user's notes.
  const [notes, setNotes] = useState('');

  const generationStartedRef = useRef(false);
  const projectNotifiedRef = useRef(false);
  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  // Mirror for livePreviews so the stream's catch (a stale closure) can tell
  // whether slides were already shown.
  const livePreviewsRef = useRef([]);
  const notesRef = useRef('');
  // Existing project row id — regeneration updates it in place instead of
  // inserting a duplicate into Recents.
  const projectIdRef = useRef(saved?.project_id || null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Cancel the SSE connection — otherwise it stays open until the
      // backend finishes, long after this workspace is gone.
      abortRef.current?.abort();
    };
  }, []);

  const requestBody = useCallback(() => JSON.stringify({
    user_id: userId,
    title: deckTitle,
    model,
    outline_slides: outlineSlides,
    special_instructions: notesRef.current || undefined,
    project_id: projectIdRef.current || undefined,
  }), [userId, deckTitle, model, outlineSlides]);

  const finishWithData = useCallback((data) => {
    if (!mountedRef.current) return;
    if (data.project_id) projectIdRef.current = data.project_id;
    setResult(data);
    setStatus('ready');
    // Fires on every completion (the parent upserts by id), so a
    // regeneration refreshes the Recents thumbnail/slide count too.
    if (data.project_id) {
      projectNotifiedRef.current = true;
      onProjectCreated?.(data.project_id, data.title || deckTitle, {
        client: 'Native PPT',
        type: 'native-ppt',
        status: 'completed',
        slideCount: data.slide_count || 0,
        thumbnail: (data.previews || []).find(Boolean) || null,
      });
    }
  }, [onProjectCreated, deckTitle]);

  // Non-streaming fallback for deploy skew (older backend without the
  // stream endpoint).
  const runLegacyGeneration = useCallback(async () => {
    const res = await fetch(
      withBackend(`/api/native-templates/${nativeTemplateId}/generate-from-outline`),
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: requestBody() }
    );
    let data = null;
    try {
      data = await res.json();
    } catch (parseErr) {
      // Non-JSON error body (proxy timeout page etc.) — fall through.
    }
    if (!res.ok || !data || data.success === false) {
      throw new Error(
        (data && (data.error || data.message)) || `Generation failed (HTTP ${res.status})`
      );
    }
    finishWithData(data);
  }, [nativeTemplateId, requestBody, finishWithData]);

  const runGeneration = useCallback(async () => {
    setStatus('generating');
    setStage('planning');
    setError(null);
    setResult(null);
    setLivePreviews([]);
    livePreviewsRef.current = [];
    const controller = new AbortController();
    abortRef.current = controller;
    // Stall guard: the longest legitimately silent gap is the planning LLM
    // call; anything past 5 minutes without a byte means a hung connection.
    let idleTimer = null;
    const armIdle = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => controller.abort(), 300000);
    };
    try {
      armIdle();
      const res = await fetch(
        withBackend(`/api/native-templates/${nativeTemplateId}/generate-from-outline-stream`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: requestBody(), signal: controller.signal }
      );
      const isStream = res.ok
        && (res.headers.get('content-type') || '').includes('text/event-stream')
        && res.body;
      if (!isStream) {
        if (res.status === 404 || res.status === 405) {
          await runLegacyGeneration();
          return;
        }
        let data = null;
        try {
          data = await res.json();
        } catch (parseErr) { /* non-JSON error body */ }
        throw new Error((data && (data.error || data.message)) || `Generation failed (HTTP ${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let completed = false;
      let streamErr = null;
      while (true) {
        const { done, value } = await reader.read();
        armIdle();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';
        for (const chunk of chunks) {
          if (!chunk.startsWith('data: ')) continue;
          let ev = null;
          try {
            ev = JSON.parse(chunk.slice(6));
          } catch (parseErr) {
            continue;
          }
          if (!mountedRef.current) return;
          if (ev.type === 'stage') {
            setStage(ev.stage);
          } else if (ev.type === 'slides') {
            // First render — show the deck immediately; QA fixes swap in via
            // later 'slides' events.
            livePreviewsRef.current = ev.previews || [];
            setLivePreviews(ev.previews || []);
            if (ev.title) setLiveTitle(ev.title);
            setStatus((s) => (s === 'generating' ? 'preview' : s));
          } else if (ev.type === 'complete') {
            completed = true;
            finishWithData(ev);
          } else if (ev.type === 'error') {
            streamErr = ev.error || 'Generation failed';
          }
        }
      }
      if (streamErr) throw new Error(streamErr);
      if (!completed) throw new Error('The generation stream ended unexpectedly.');
    } catch (err) {
      if (!mountedRef.current) return;
      const message = err?.name === 'AbortError'
        ? 'The generation stalled — check your connection and retry.'
        : err?.message || 'Something went wrong while filling the template.';
      if (livePreviewsRef.current.length) {
        // Slides already rendered — keep showing them instead of replacing
        // the deck with a full-screen error. The header offers a retry.
        setStage('failed');
        setStatus('preview');
        toast.error(`Couldn't finish the deck: ${message}`);
      } else {
        setError(message);
        setStatus('error');
      }
    } finally {
      clearTimeout(idleTimer);
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [nativeTemplateId, requestBody, runLegacyGeneration, finishWithData]);

  // Kick off generation exactly once on mount (unless viewing a saved deck).
  useEffect(() => {
    if (saved) return;
    if (generationStartedRef.current) return;
    generationStartedRef.current = true;
    runGeneration();
  }, [saved, runGeneration]);

  // Live elapsed-seconds counter while generating (resets on retry too,
  // since status cycles error -> generating).
  useEffect(() => {
    if (status !== 'generating') return undefined;
    const startedAt = Date.now();
    setElapsed(0);
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [status]);

  // Normalized view model shared by generate, live-preview, and saved modes.
  // Precedence: a completed result beats everything; during an ACTIVE
  // regeneration the streaming previews beat the saved snapshot; otherwise
  // the saved deck shows.
  const view = useMemo(() => {
    const live = livePreviews.length
      ? { title: liveTitle, previews: livePreviews, qa_fixes: null, download_url: null }
      : null;
    const source = result || (status === 'preview' ? live : null) || saved || live;
    if (!source) return null;
    const rawPreviews = Array.isArray(source.previews) ? source.previews : [];
    const slideCount = rawPreviews.length || source.slide_count || 0;
    const previews = rawPreviews.length
      ? rawPreviews
      : Array.from({ length: slideCount }, () => null);
    return {
      title: source.title || deckTitle || 'Untitled deck',
      previews,
      downloadUrl: source.download_url ? withBackend(source.download_url) : null,
      qaFixes: Array.isArray(source.qa_fixes) ? source.qa_fixes : null,
    };
  }, [saved, result, livePreviews, liveTitle, deckTitle, status]);

  const total = view ? view.previews.length : 0;

  // Keep the selection valid if the preview list changes.
  useEffect(() => {
    setSelectedIndex((i) => Math.min(Math.max(i, 0), Math.max(total - 1, 0)));
  }, [total]);

  const goPrev = useCallback(() => {
    setSelectedIndex((i) => Math.max(i - 1, 0));
  }, []);

  const goNext = useCallback(() => {
    setSelectedIndex((i) => Math.min(i + 1, Math.max(total - 1, 0)));
  }, [total]);

  // Keyboard navigation: arrows in viewer and present mode, Escape exits
  // present mode. Ignores keystrokes aimed at form fields elsewhere in the app.
  useEffect(() => {
    const handler = (e) => {
      const t = e.target;
      if (
        t &&
        (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)
      ) {
        return;
      }
      if (presentMode && e.key === 'Escape') {
        e.preventDefault();
        setPresentMode(false);
        return;
      }
      // Arrow navigation only applies to present mode — the viewer itself is
      // a scrolling feed like the HTML deck view.
      if (!presentMode) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [presentMode, status, goPrev, goNext]);

  // Lock body scroll behind the fullscreen present overlay.
  useEffect(() => {
    if (!presentMode) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [presentMode]);


  useImperativeHandle(ref, () => ({
    save: () => toast.success('Native decks save automatically'),
    present: () => {
      // Mid-generation there is nothing to present — and a stale flag would
      // pop the fullscreen overlay unexpectedly when generation completes.
      if (status === 'ready' || status === 'preview' || saved) setPresentMode(true);
      else toast('The deck is still generating…');
    },
  }), [status, saved]);

  // ---------------- GENERATING ----------------
  if (status === 'generating') {
    const STAGE_STEPS = [
      ['planning', 'Planning content onto the template layouts'],
      ['filling', 'Filling the master template'],
      ['rendering', 'Rendering slides'],
    ];
    const stageIdx = Math.max(0, STAGE_STEPS.findIndex(([key]) => key === stage));
    return (
      <div className="flex h-full min-h-[480px] w-full flex-1 flex-col items-center justify-center gap-5 rounded-xl border border-border bg-card p-10 text-center">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" aria-hidden="true" />
        <div>
          <p className="text-base font-medium text-foreground">
            Filling your PowerPoint template natively…
          </p>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Slides appear here the moment they render.
          </p>
        </div>
        <ul className="flex flex-col items-start gap-1.5 text-left text-sm">
          {STAGE_STEPS.map(([key, label], i) => (
            <li key={key} className="flex items-center gap-2">
              {i < stageIdx ? (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">✓</span>
              ) : i === stageIdx ? (
                <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
              ) : (
                <span className="h-4 w-4 rounded-full border border-border" />
              )}
              <span className={i <= stageIdx ? 'text-foreground' : 'text-muted-foreground'}>
                {label}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-xs tabular-nums text-muted-foreground">{elapsed}s elapsed</p>
      </div>
    );
  }

  // ---------------- ERROR ----------------
  if (status === 'error') {
    return (
      <div className="flex h-full min-h-[480px] w-full flex-1 flex-col items-center justify-center gap-4 rounded-xl border border-border bg-card p-10 text-center">
        <AlertCircle className="h-10 w-10 text-destructive" aria-hidden="true" />
        <div>
          <p className="text-base font-medium text-foreground">
            Couldn&apos;t fill the template
          </p>
          <p className="mt-1 max-w-md break-words text-sm text-muted-foreground">
            {error}
          </p>
        </div>
        <button
          type="button"
          onClick={runGeneration}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Retry
        </button>
      </div>
    );
  }

  if (!view) return null;

  // Live previews are data URLs (straight from the render, pre-upload);
  // saved/complete previews are storage URLs that need backend resolution.
  const resolvePreview = (p) => (p ? (p.startsWith('data:') ? p : withBackend(p)) : null);

  const currentRaw = view.previews[selectedIndex] || null;
  const currentSrc = resolvePreview(currentRaw);
  const finishing = status === 'preview';
  const finishingLabel = {
    qa: 'Polishing slides…',
    replanning: 'Fixing broken slides…',
    filling: 'Refilling template…',
    rendering: 'Rendering slides…',
  }[stage] || 'Finalizing…';

  // ---------------- VIEWER ----------------
  // Layout mirrors the HTML deck view users actually see (CreatePPTWorkspace
  // externalChatMode): a vertical scrolling feed of full-width slide cards.
  // Clicking a slide opens present mode at that slide.
  return (
    <div className="flex h-full w-full flex-col gap-3">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-lg font-semibold text-foreground">{view.title}</h2>
          <p className="text-xs text-muted-foreground">
            Native PowerPoint — already a .pptx, no export needed
            <span className="mx-2 text-muted-foreground/50">·</span>
            {total} slides
          </p>
          {view.qaFixes && view.qaFixes.length > 0 && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              QA auto-fixed {view.qaFixes.length} slide issue(s)
            </p>
          )}
        </div>
        {finishing ? (
          stage === 'failed' ? (
            <button
              type="button"
              onClick={runGeneration}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/20"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Couldn&apos;t finish — Retry
            </button>
          ) : (
            <span className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border bg-muted px-4 py-2 text-sm font-medium text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {finishingLabel}
            </span>
          )
        ) : view.downloadUrl && (
          <a
            href={view.downloadUrl}
            download
            target="_blank"
            rel="noopener"
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Download .pptx
          </a>
        )}
      </div>

      {/* Slide feed — full-width cards, scroll to browse, click to present */}
      <div className="min-h-0 flex-1 space-y-8 overflow-y-auto px-1 py-2 md:px-6">
        {view.previews.map((preview, i) => {
          const src = resolvePreview(preview);
          return (
            <div key={i} className="mx-auto w-full max-w-4xl">
              <div className="mb-1.5 flex items-baseline gap-2">
                <span className="text-sm font-medium text-foreground">Slide {i + 1}</span>
                <span className="text-xs text-muted-foreground">of {total}</span>
              </div>
              <button
                type="button"
                onClick={() => { setSelectedIndex(i); setPresentMode(true); }}
                aria-label={`Present from slide ${i + 1}`}
                className="block w-full cursor-zoom-in overflow-hidden rounded-lg border border-slate-200 bg-slate-100 transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
              >
                <div className="aspect-video w-full">
                  {src ? (
                    <img
                      src={src}
                      alt={`Slide ${i + 1}`}
                      loading="lazy"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
                      <ImageOff className="h-8 w-8" aria-hidden="true" />
                      <span className="text-sm">Preview unavailable for slide {i + 1}</span>
                    </div>
                  )}
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {/* Regenerate with notes — the only way to change a rendered .pptx is
          re-running the native engine. Needs the outline + template id. */}
      {status === 'ready' && outlineSlides?.length > 0 && nativeTemplateId && (
        <div className="mx-auto flex w-full max-w-4xl items-center gap-2 rounded-xl border border-border bg-card p-2">
          <input
            type="text"
            value={notes}
            onChange={(e) => { setNotes(e.target.value); notesRef.current = e.target.value; }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                runGeneration();
              }
            }}
            placeholder="Describe changes (e.g. 'shorter titles, more numbers on slide 3'), then regenerate…"
            className="min-w-0 flex-1 bg-transparent px-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
          />
          <button
            type="button"
            onClick={runGeneration}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-muted px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted/70"
            title="Re-runs the native fill with your notes (~1-2 minutes)"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Regenerate deck
          </button>
        </div>
      )}

      {/* Present mode */}
      {presentMode && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black">
          {currentSrc ? (
            <img
              src={currentSrc}
              alt={`Slide ${selectedIndex + 1}`}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-white/70">
              <ImageOff className="h-10 w-10" aria-hidden="true" />
              <span className="text-sm">
                Preview unavailable for slide {selectedIndex + 1}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={() => setPresentMode(false)}
            aria-label="Exit presentation"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
          {total > 1 && (
            <>
              <button
                type="button"
                onClick={goPrev}
                disabled={selectedIndex === 0}
                aria-label="Previous slide"
                className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 disabled:pointer-events-none disabled:opacity-30"
              >
                <ChevronLeft className="h-6 w-6" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={goNext}
                disabled={selectedIndex >= total - 1}
                aria-label="Next slide"
                className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 disabled:pointer-events-none disabled:opacity-30"
              >
                <ChevronRight className="h-6 w-6" aria-hidden="true" />
              </button>
            </>
          )}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-sm tabular-nums text-white/90">
            {selectedIndex + 1} of {total}
          </div>
        </div>
      )}
    </div>
  );
});

export default NativePPTWorkspace;
